import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Heading from "../../elements/heading";
import Checkbox from "../../elements/checkbox";
import PrimaryButton from "../../elements/primary-button";
import BodyText from "../../elements/body-text";
import api from "../../utils/api";
import { useSelector } from "react-redux";
import RadioButtonGroup from "../../elements/radio-button-group";
import ArvatoWidget from "../arvato-widget";
import { Icon } from "../../elements";
import BankCardInformation from "../bank-card-information";
import ConfirmationModal from "../../react-components/confirmation-modal";
import PrimaryLink from "../../elements/primary-link";
import { selectUserState } from "../../selectors/application";
import { S_GEAR_SAVE_PAYMENT, webStorage } from "../../utils/webStorage";
import useFetchBankCards from "../../hooks/useFetchBankCards";
import "./style.scss";
const CarBookingCardInfo = ({ onSubmit, fields }) => {
  /*Redux Start*/
  const user = useSelector(selectUserState);
  /*Redux End*/
  const {
    arvatoOptions,
    heading,
    headingType,
    submitCTA,
    removeCardModal,
    termsCheckable,
    termsConditionsLabel,
    termsConditionsBody,
    savePaymentLabel,
    savePaymentBody,
    disclaimer,
    paymethodMissingMessage,
    paymentSavable,
    ctaNameAddCard,
    colorSchemeAddCard,
    iconNameAddCard,
    cardExpiredText,
    oldDriverCheckable,
    oldDriverLabel,
    oldDriverBody
  } = fields;
  const { link: linkSubmit, iconName: iconNameSubmit, colorScheme: colorSchemeSubmit } = submitCTA.fields;
  const { href: orderConfirmationUrl, text: ctaNameSubmit } = linkSubmit.value;
  const arvatoWidget = useRef();
  const isAgreeToTerms = useRef();
  const { bankCards, selectedBankCard, setSelectedBankCard, showCardForm, setShowCardForm, getBankCards } = useFetchBankCards({
    onFetchEmpty() {
      arvatoWidget.current.init();
    }
  });
  const [submitting, setSubmitting] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [agreedToTermsHasError, setAgreedToTermsHasError] = useState(false);
  const [savePayment, setSavePayment] = useState(false);
  const [isOldDriver, setIsOldDriver] = useState(false);
  const [oldDriverHasError, setOldDriverHasError] = useState(false);
  const [editCard, setEditCard] = useState(null);
  const [showRemoveCardModal, setShowRemoveCardModal] = useState(false);
  const [removeCardError, setRemoveCardError] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    getBankCards();
    return () => {
      webStorage.clear(S_GEAR_SAVE_PAYMENT);
    };
  }, []);
  useEffect(() => {
    webStorage.set(S_GEAR_SAVE_PAYMENT, savePayment);
  }, [savePayment]);
  const termsProps = {
    label: termsConditionsLabel.value,
    infoText: termsConditionsBody.value,
    error: agreedToTermsHasError,
    checked: agreeToTerms,
    onChange: checked => {
      setAgreeToTerms(checked);
      isAgreeToTerms.current = checked;
      setAgreedToTermsHasError(false);
    }
  };
  const paymentInfoProps = {
    label: savePaymentLabel.value,
    infoText: savePaymentBody.value,
    checked: savePayment,
    onChange: checked => {
      setSavePayment(checked);
    }
  };
  const submitButtonProps = {
    buttonText: ctaNameSubmit,
    iconName: iconNameSubmit,
    colorScheme: colorSchemeSubmit,
    isDisabled: submitting,
    callback: async evt => {
      let isValid = true;
      if (!selectedBankCard && !showCardForm) {
        setError(paymethodMissingMessage.value);
        isValid = false;
      }
      if (termsCheckable.value && !isAgreeToTerms.current) {
        setAgreedToTermsHasError(true);
        isValid = false;
      }
      if (oldDriverCheckable.value && !isOldDriver) {
        setOldDriverHasError(true);
        isValid = false;
      }
      setSubmitting(true);
      if (selectedBankCard) {
        console.log("selectedBankCard.card", selectedBankCard.data);
        await onSubmit({ isValid, paymentRegistrationToken: selectedBankCard.data.registrationNo });
        setSubmitting(false);
      } else if (showCardForm) {
        arvatoWidget.current.submit();
      }
    }
  };
  const onRemoveCardClick = (evt, card) => {
    evt.stopPropagation();
    setEditCard(card);
    setShowRemoveCardModal(true);
  };
  const onBankCardChange = option => {
    setSelectedBankCard(option);
    setShowCardForm(false);
    setError(null);
  };
  const oldDriverProps = {
    label: oldDriverLabel.value,
    infoText: oldDriverBody.value,
    error: oldDriverHasError,
    checked: isOldDriver,
    onChange: checked => {
      setIsOldDriver(checked);
      setOldDriverHasError(false);
    }
  };
  const addCardCtaProps = {
    buttonText: ctaNameAddCard.value,
    colorScheme: colorSchemeAddCard,
    iconName: iconNameAddCard,
    callback() {
      setShowCardForm(true);
      setSelectedBankCard(null);
      arvatoWidget.current.init();
    }
  };
  const arvatoWidgetProps = {
    arvatoOptions: arvatoOptions.fields,
    visible: showCardForm,
    ref: arvatoWidget,
    user: user,
    onSubmit: async paymentToken => {
      let isValid = true;
      if (termsCheckable.value && !isAgreeToTerms.current) {
        setAgreedToTermsHasError(true);
        isValid = false;
      }
      if (oldDriverCheckable.value && !isOldDriver) {
        setAgreedToTermsHasError(true);
        isValid = false;
      }
      setSubmitting(true);
      await onSubmit({ isValid, paymentToken, savePayment });
      setSubmitting(false);
    }
  };
  const removeCardModalProps = removeCardModal && {
    heading: {
      type: removeCardModal.fields.headingType,
      html: removeCardModal.fields.headingBody
    },
    subHeading: {
      type: removeCardModal.fields.subheadingType,
      html: removeCardModal.fields.subheadingBody
    },
    body: removeCardModal.fields.body.value,
    button: {
      ...{
        colorScheme: removeCardModal.fields.colorScheme,
        iconName: removeCardModal.fields.iconName,
        link: {
          value: {
            text: removeCardModal.fields.ctaName.value
          }
        },
        callback: async evt => {
          await api.user.deleteBankCard(null, { bankCardIds: [editCard.id] });
          setShowRemoveCardModal(false);
          setEditCard(null);
          await getBankCards();
        }
      }
    },
    warningMessage: removeCardError,
    onCloseClicked: () => {
      setShowRemoveCardModal(false);
      setRemoveCardError(null);
    },
    showModal: showRemoveCardModal
  };
  const bankCardLayout = option => {
    if (option.data) {
      return (
        <div className="bank-card-contianer">
          <BankCardInformation {...{ card: option.data, expiredText: cardExpiredText }} />
          <div className="icons">{selectedBankCard !== option && bankCards.length > 1 && <Icon name="Delete" colorScheme="outline-green" className="icon --visible" onClick={evt => onRemoveCardClick(evt, option.data)} />}</div>
        </div>
      );
    } else {
      return <p className="label">{option.label}</p>;
    }
  };
  return (
    <section className="card-info">
      <div className="title">
        <Heading type={headingType} html={heading} />
      </div>
      <RadioButtonGroup options={bankCards} html={bankCardLayout} alignment="top" onChange={e => onBankCardChange(e)} selectedOption={selectedBankCard} />
      {!showCardForm && (
        <div className="add-payment-cta">
          <PrimaryLink {...addCardCtaProps} />
        </div>
      )}
      <div className="widget-container">
        <ArvatoWidget {...arvatoWidgetProps} />
      </div>
      {termsCheckable.value && <Checkbox {...termsProps} />}
      {oldDriverCheckable.value && <Checkbox {...oldDriverProps} />}
      {paymentSavable.value && user?.userAccount && !selectedBankCard && <Checkbox {...paymentInfoProps} />}
      {error && <div className="failed-message">{error}</div>}
      <div className="submit">
        <PrimaryButton {...submitButtonProps} />
      </div>
      <BodyText html={disclaimer.value} className="body-text" />
      <ConfirmationModal {...removeCardModalProps} />
    </section>
  );
};
export default CarBookingCardInfo;

CarBookingCardInfo.propTypes = {
  fields: PropTypes.object,
  onSubmit: PropTypes.func
};
